import React, { FC, useCallback, useState } from 'react';
import { useSnapshot } from 'valtio';
import AppSpinner from '../../common/components/AppSpinner';
import { Connector } from '../../connectors/connectors-types';
import {
  handleAdvancedOptionsInputChange,
  handleCodeRepoSelection,
  handleConnect,
} from '../actions/onboarding-actions';
import {
  setOrgName,
  setUseIDP,
} from '../store-actions/onboarding-store-actions';
import OnBoardingForm from '../components/OnBoardingForm';
import OnboardingStore from '../stores/onboarding-store';
import { Typography } from '@mui/material';
import { startDemoScan } from '../actions/onboarding-actions';
import { datadogLogs } from '@datadog/browser-logs';
import { useAuth0 } from '@auth0/auth0-react';
import { submitHubSpotForm } from '../../hubspot-analytics/actions/hubspot-analytics-actions';
import {
  HubSpotOxAppFormFields,
  HubSpotFormsIds,
  HubSpotOxBaseFormFields,
} from '../../hubspot-analytics/hubspot-types';
import { setDemoEnabled } from '../../new-scan/store/scan-store-actions';

const OnboardingConnectorsContainer: FC<
  React.PropsWithChildren<OnboardingCodeRepoAndOrgProps>
> = ({ connectors, creatingOrgName }) => {
  const {
    chosenCodeRepo,
    isLoading,
    isOnboardingInitInProcess,
    hostUrl,
    tokenOrPassword,
    disableConnect,
    onboardingConnectorsError,
    orgName,
    username,
    connectTooltipMsg,
    validationError,
    isValidOrgDisplayName,
    invalidOrgDisplayNameHelperText,
  } = useSnapshot(OnboardingStore);
  const { user } = useAuth0();
  const [advancedOptionsOpen, setAdvancedOptionsOpen] = useState(false);

  const handleAdvancedOptions = () => {
    setAdvancedOptionsOpen(!advancedOptionsOpen);
  };

  const handleRepoButtonClick = useCallback((repoName: string) => {
    handleCodeRepoSelection(repoName);
  }, []);

  const handleSelectUseToken = useCallback((shouldUseToken: boolean) => {
    setUseIDP(!shouldUseToken);
  }, []);

  const onOrgChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setOrgName(event.target.value);
    },
    [],
  );

  const onDemoScanClick = useCallback(() => {
    datadogLogs.logger.info('Onboarding: Demo data requested', {
      demoScanCalled: true,
    });
    setDemoEnabled(true);
    if (user) {
      submitHubSpotForm(
        {
          [HubSpotOxBaseFormFields.Email]: user.email!,
          [HubSpotOxBaseFormFields.FirstName]: user.given_name || user.name,
          [HubSpotOxBaseFormFields.LastName]: user.family_name,
          [HubSpotOxAppFormFields.DemoDataRequested]: true,
        },
        HubSpotFormsIds.OxAppForm,
      );
    }
    startDemoScan();
  }, [user]);

  return (
    <>
      {isOnboardingInitInProcess && (
        <AppSpinner
          text={`Creating new organization - ${creatingOrgName}`}
          disablePositionAbsolute
        />
      )}
      {!isOnboardingInitInProcess && (
        <>
          {onboardingConnectorsError && (
            <Typography variant='body1' color='error' sx={{ maxWidth: 250 }}>
              {onboardingConnectorsError}
            </Typography>
          )}
          <OnBoardingForm
            hostUrlValue={hostUrl}
            tokenOrPasswordValue={tokenOrPassword}
            usernameValue={username}
            onAdvanceOptionsInputChange={handleAdvancedOptionsInputChange}
            onAdvancedOptionChange={handleSelectUseToken}
            connectors={connectors}
            connecting={isLoading}
            openAdvancedOptions={advancedOptionsOpen}
            handleOpenAdvancedOptions={handleAdvancedOptions}
            organizationName={orgName || ''}
            selectedConnector={chosenCodeRepo}
            onClick={handleRepoButtonClick}
            onConnect={handleConnect}
            disableConnect={disableConnect}
            onOrgChange={onOrgChange}
            connectTooltipMsg={connectTooltipMsg}
            repoError={validationError || undefined}
            onDemoScanClick={onDemoScanClick}
            isValidOrgDisplayName={isValidOrgDisplayName}
            invalidOrgDisplayNameHelperText={invalidOrgDisplayNameHelperText}
          />
        </>
      )}
    </>
  );
};

interface OnboardingCodeRepoAndOrgProps {
  connectors: Connector[];
  creatingOrgName: string;
}

export default OnboardingConnectorsContainer;
